import { defineStore } from "pinia";
import { createBaseStoreRefactor } from "./baseStoreRefactor";
import apiClient from "../services/apiClient";

// Create base store configuration for reports
const reportsBaseStore = createBaseStoreRefactor({
  endpoint: "/api/v1/reports",
  name: "ReportsStoreRefactor",
  objectName: "Rapport", // Auto-generates CRUD success messages
  enableCache: false,
  actionCableChannel: "ReportChannel", // Use class name (singular)
  infiniteLoading: true,
});

export const useReportsStoreRefactor = defineStore("reportsRefactor", {
  ...reportsBaseStore,

  state: () => ({
    ...reportsBaseStore.state(),
    // Note: Report category icons/colors are now managed dynamically
    // via useReportCategoryIcons composable and report_categories database table
  }),

  getters: {
    ...reportsBaseStore.getters,

    /**
     * Alias for reports to maintain backward compatibility
     */
    reports: (state) => state.data,

    /**
     * Get reports by day report ID with proper sorting
     */
    getReportsByDayReport: (state) => (dayReportId) => {
      return state.data
        .filter((report) => report.day_report_id == dayReportId)
        .sort((a, b) => {
          // First sort by sticky status (sticky reports first)
          if (a.sticky && !b.sticky) return -1;
          if (!a.sticky && b.sticky) return 1;

          // Then sort by date (newest first)
          const dateA = new Date(a.date || a.created_at);
          const dateB = new Date(b.date || b.created_at);
          return dateB - dateA;
        });
    },

    /**
     * Get reports by account ID with proper sorting
     */
    getReportsByAccount: (state) => (accountId) => {
      return state.data
        .filter((report) => report.account_id == accountId)
        .sort((a, b) => {
          // First sort by sticky status (sticky reports first)
          if (a.sticky && !b.sticky) return -1;
          if (!a.sticky && b.sticky) return 1;

          // Then sort by date (newest first)
          const dateA = new Date(a.date || a.created_at);
          const dateB = new Date(b.date || b.created_at);
          return dateB - dateA;
        });
    },

    /**
     * Get reports for a specific account and day report in chronological order
     * Used by AccountBox to show reports from oldest to newest
     */
    getReportsByAccountAndDayReport: (state) => (accountId, dayReportId) => {
      return state.data
        .filter(
          (report) =>
            report.account_id == accountId &&
            report.day_report_id == dayReportId,
        )
        .sort((a, b) => {
          // Sort chronologically (oldest first)
          const dateA = new Date(a.date || a.created_at);
          const dateB = new Date(b.date || b.created_at);
          return dateA - dateB;
        });
    },

    /**
     * Get sticky reports
     */
    stickyReports: (state) => {
      return state.data.filter((report) => report.sticky === true);
    },
  },

  actions: {
    ...reportsBaseStore.actions,

    /**
     * Get reports for a specific day report with smart caching
     * @param {Number} dayReportId - The day report ID to fetch reports for
     * @param {Object} options - Options for loading behavior
     * @returns {Array} Array of reports for the day report
     */
    async getByDayReport(dayReportId, options = {}) {
      if (!dayReportId) {
        throw new Error("Day report ID is required");
      }

      const { forceRefresh = false, silent = false } = options;

      // Check if we already have reports for this day report (unless forcing refresh)
      if (!forceRefresh) {
        const existingReports = this.getReportsByDayReport(dayReportId);
        if (existingReports.length > 0) {
          if (!silent) {
            console.log(
              `ReportsStore: Found ${existingReports.length} existing reports for day_report ${dayReportId}`,
            );
          }
          return existingReports;
        }
      }

      if (!silent) {
        console.log(
          `ReportsStore: Fetching reports for day_report ${dayReportId}${forceRefresh ? " (force refresh)" : ""}`,
        );
      }

      this.loading = true;
      this.error = null;

      try {
        const response = await apiClient.get(
          `/api/v1/day_reports/${dayReportId}/reports`,
          {},
          { showErrorAlert: false },
        );

        // Handle both direct array and paginated response formats
        let reports;
        if (Array.isArray(response)) {
          // Direct array response
          reports = response;
        } else if (response && Array.isArray(response.data)) {
          // Paginated response format: { data: [...], meta: {...} }
          reports = response.data;
        } else {
          // Enhanced error logging for debugging
          console.error("ReportsStore: Invalid response format:", {
            type: typeof response,
            isArray: Array.isArray(response),
            hasData: response && "data" in response,
            dataType: response && typeof response.data,
            keys: response ? Object.keys(response) : "null/undefined",
            response: response,
          });
          throw new Error(
            `Invalid response format: expected array or {data: array}, got ${typeof response}. Check console for details.`,
          );
        }

        // Add the reports to our store (base store handles deduplication)
        reports.forEach((report) => {
          this._upsertItem(report);
        });

        if (!silent) {
          console.log(
            `ReportsStore: Loaded ${reports.length} reports for day_report ${dayReportId}`,
          );
        }

        return reports;
      } catch (error) {
        this.error =
          error.message ||
          "Er is een fout opgetreden bij het laden van rapporten";
        console.error(
          `ReportsStore: Error fetching reports for day_report ${dayReportId}:`,
          error,
        );

        // Return empty array on error to prevent UI breaks
        return [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * Preload reports for multiple day reports (batch optimization)
     * @param {Array} dayReportIds - Array of day report IDs
     */
    async preloadReportsForDayReports(dayReportIds) {
      if (!Array.isArray(dayReportIds) || dayReportIds.length === 0) {
        return;
      }

      console.log(
        `ReportsStore: Preloading reports for ${dayReportIds.length} day reports`,
      );

      // Load in parallel but limit concurrency to avoid overwhelming the server
      const BATCH_SIZE = 3;
      const batches = [];

      for (let i = 0; i < dayReportIds.length; i += BATCH_SIZE) {
        batches.push(dayReportIds.slice(i, i + BATCH_SIZE));
      }

      for (const batch of batches) {
        await Promise.all(
          batch.map((dayReportId) =>
            this.getByDayReport(dayReportId, { silent: true }).catch((err) => {
              console.warn(
                `Failed to preload reports for day_report ${dayReportId}:`,
                err,
              );
              return [];
            }),
          ),
        );
      }

      console.log(`ReportsStore: Completed preloading reports for day reports`);
    },

    /**
     * Get reports with account data populated (for dashboard views)
     * @param {Number} dayReportId - The day report ID
     * @param {Object} accountsStore - Accounts store instance for populating account data
     */
    async getReportsWithAccountsForDayReport(dayReportId, accountsStore) {
      const reports = await this.getByDayReport(dayReportId);

      // Populate account data for each report
      return reports.map((report) => ({
        ...report,
        account: report.account_id
          ? accountsStore.getById(report.account_id)
          : null,
      }));
    },

    // Ensure trait_ids are sent (map from legacy selected_traits when needed)
    async create(item) {
      const payload = { ...item };
      if (!payload.trait_ids && Array.isArray(payload.selected_traits)) {
        payload.trait_ids = payload.selected_traits;
      }
      const data = await apiClient.post(
        "/api/v1/reports",
        payload,
        {},
        { showErrorAlert: true },
      );
      // Add the new report to local state immediately so it is visible without
      // waiting for the ActionCable echo. The websocket broadcast still arrives,
      // but _upsertItem de-duplicates by id, so it will not create a duplicate.
      this._upsertItem(data, true); // prepend (newest first)
      return data;
    },

    async update(item) {
      if (!item?.id) throw new Error("Cannot update report without id");
      const payload = { ...item };
      if (!payload.trait_ids && Array.isArray(payload.selected_traits)) {
        payload.trait_ids = payload.selected_traits;
      }
      const data = await apiClient.patch(
        `/api/v1/reports/${item.id}`,
        payload,
        {},
        { showErrorAlert: true },
      );
      // Reflect the edit in local state immediately (same rationale as create).
      this._upsertItem(data);
      return data;
    },
  },
});
